import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthFlowState, AuthFlowStateEventType, FlowType, AuthResult, ScreenType } from 'src/app/models/pin-code.model';
import { AlertService } from 'src/app/services/alert/alert.service';
import { AuthFlowService } from 'src/app/services/auth-flow/auth-flow.service';
import { EventBusService, EventTopic } from 'src/app/services/event-bus/event-bus.service';
import { VaultService } from 'src/app/services/vault/vault.service';
import { PIN_LENGTH } from 'src/config';

@Component({
  selector: 'app-enter-pin',
  templateUrl: './enter-pin.component.html',
  styleUrls: ['./enter-pin.component.scss'],
})
export class EnterPinComponent implements OnInit, OnDestroy {
  @Input() pin: string;
  @Input() state: AuthFlowState;
  @Input() isOnline: boolean;
  pinBox = Array.from({ length: PIN_LENGTH }, (_, i) => i + 1);
  errorMessage: AuthResult = AuthResult.noError;
  readonly pinEnterMaxAttempts = 5;

  constructor(
    public authFlow: AuthFlowService,
    public vault: VaultService,
    public translate: TranslateService,
    public alertSvc: AlertService,
    private eventBusSvc: EventBusService
  ) {}

  ngOnInit() {
    this.state.event$.subscribe(event => {
      if (event.type === AuthFlowStateEventType.clearPin) {
        this.clearPinAndError();
      }
    });
    this.eventBusSvc.addSubscriber(
      EventTopic.AuthFlowStateEventType,
      event => {
        if (event.type === AuthFlowStateEventType.clearPin) {
          this.clearPinAndError();
        }
      },
      EnterPinComponent.name
    );
  }

  ngOnDestroy(): void {
    this.eventBusSvc.deleteSubscriber(EventTopic.AuthFlowStateEventType, EnterPinComponent.name);
  }

  get disableEnter(): boolean {
    return this.pin?.length !== this.pinBox.length || !this.isOnline;
  }

  get disableDelete(): boolean {
    return !this.pin?.length || !this.isOnline;
  }

  get disableInput(): boolean {
    return this.pin?.length === this.pinBox.length || !this.isOnline;
  }

  get disableForgotPin(): boolean {
    return !this.isOnline;
  }

  async append(n: number) {
    this.errorMessage = AuthResult.noError;
    this.pin = this.pin?.concat(n.toString());
    if (this.disableInput) {
      const loading = await this.alertSvc.createLoadingAlert();
      await loading.present();
      this.errorMessage = (await this.authFlow.enterPin(this.pin)) || AuthResult.noError;
      await loading.dismiss();

      if (this.errorMessage && this.authFlow.retryCount <= 2 && [FlowType.login, FlowType.change].includes(this.authFlow.flowType)) {
        await this.warn();
      }
      if (this.state.currentScreen === ScreenType.login) {
        this.pin = this.authFlow.pin;
      } else if (this.state.currentScreen === ScreenType.validate) {
        this.pin = this.authFlow.validatePin;
      }
    }
  }

  delete() {
    if (this.pin) {
      this.errorMessage = AuthResult.noError;
      this.pin = this.pin.slice(0, this.pin.length - 1);
    }
  }

  async forgotPin() {
    const alert = await this.alertSvc.presentCustomConfirmation(
      this.translate.instant('PinPage.forgotPinHeader'),
      '',
      this.translate.instant('PinPage.resetPinConfirmation'),
      this.translate.instant('CommonTexts.cancel'),
      this.translate.instant('PinPage.yesReset'),
      '',
      true
    );
    if (alert.option) {
      this.eventBusSvc.publish(EventTopic.AuthFlowStateEventType, { type: AuthFlowStateEventType.forgotPin });
    }
  }

  async warn() {
    const attemptNumber = this.pinEnterMaxAttempts - this.authFlow.retryCount;
    const translateParam = {
      LEFT: this.authFlow.retryCount,
      RETRIES: attemptNumber,
    };
    let description = this.translate.instant(
      `PinPage.wrongPinDescriptionCanTry` + (this.authFlow.retryCount <= 1 ? '1Left' : ''),
      translateParam
    );
    switch (this.authFlow.retryCount) {
      case 2:
        description += '<br><br>' + this.translate.instant(`PinPage.wrongPinDescriptionForgot`);
        break;
      case 1:
        description += '<br><br>' + this.translate.instant(`PinPage.wrongPinDescription1More`);
        description += '<br><br>' + this.translate.instant(`PinPage.wrongPinDescriptionForgot`);
        break;
      case 0:
        description = this.translate.instant(`PinPage.wrongPinDescriptionLocked`, translateParam);
        description += '<br><br>' + this.translate.instant(`PinPage.wrongPinDescriptionSentReset`);
        break;
    }

    await this.alertSvc.presentCustomConfirmation(
      this.translate.instant(`PinPage.wrongPinHeader`),
      '',
      description,
      '',
      this.translate.instant(`CommonTexts.ok`),
      '',
      true
    );
    if (!this.authFlow.retryCount) {
      this.authFlow.locked = true;
    }
  }

  clearPinAndError() {
    this.pin = '';
    this.errorMessage = AuthResult.noError;
  }
}
